import * as React from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Await,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigation,
} from "react-router-dom";
import { GetLoanHistoryReturn } from "@tlc/ls2pac/dto/GetLoanHistoryReturn";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import {
  isLoanHistorySortType,
  LoanHistorySortType,
} from "./HostSystemItemSortControl";
import { getValidatedPage } from "./getValidatedPage";
import { SharedLoanHistoryView } from "./SharedLoanHistoryView";
import { LSResultItemSkeletonList } from "../search/ls/LSResultItem/LSResultItemSkeletonList";
import { LSResultItemListWithToolbarSkeleton } from "../search/ls/LSResultItem/LSResultItemListWithToolbarSkeleton";
import { LSResultsSelectionProvider } from "../search/LSResultsSelectionContext";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";

export interface BorrowerHistoryLoaderData {
  id: string;
  page: number;
  sort: HostSystemItemSortType;
  pageSize: number;
  totalLoans: number;
  loanHistory: GetLoanHistoryReturn["loanHistory"];
}

export async function BorrowerLoanHistoryLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const id = params.id!;
  const pageSize: number = 10;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort =
    sortParam && isLoanHistorySortType(sortParam)
      ? sortParam
      : HostSystemItemSortType.OutDate;
  const start = (page - 1) * pageSize;

  const dataPromise = Promise.all([
    axios.get<{ count: number }>(`/loans/history/count`),
    axios.get<GetLoanHistoryReturn>(
      `/loans/history/${start}/${pageSize}/${sort}/true`,
    ),
  ])
    .then(([countRes, historyRes]) => {
      const resolvedData: BorrowerHistoryLoaderData = {
        id,
        page,
        sort,
        pageSize,
        totalLoans: countRes.data.count,
        loanHistory: historyRes.data.loanHistory,
      };
      return resolvedData;
    })
    .catch((error) => {
      console.error("Error fetching loan history", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return { data: dataPromise, pageSize };
}

export const fetchLoanHistoryRange = async (
  startDate?: Date,
  endDate?: Date,
): Promise<HostSystemItemView[]> => {
  if (!startDate || !endDate) return [];
  const { data } = await axios.post<GetLoanHistoryReturn>(
    `/loans/history/byDateRange`,
    {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      includeResources: false,
    },
  );
  return data.loanHistory;
};

export const BorrowerHistoryView: React.FC = () => {
  const { data, pageSize } = useLoaderData() as {
    data: Promise<typeof BorrowerLoanHistoryLoader>;
    pageSize: number;
  };

  const getId = React.useCallback(
    (item: ResourceView | ResourceDWR) => item.id,
    [],
  );

  return (
    <React.Suspense
      fallback={
        <LSResultItemListWithToolbarSkeleton
          pageSize={pageSize}
          hideCheckbox={true}
          hideActions={false}
        />
      }
    >
      <Await resolve={data}>
        {(resolved: BorrowerHistoryLoaderData) => {
          const items = resolved.loanHistory.flatMap((loan: any) =>
            loan.resource ? [loan.resource] : [],
          );

          return (
            <LSResultsSelectionProvider items={items} getId={getId}>
              <SharedLoanHistoryView
                page={resolved.page}
                pageSize={resolved.pageSize}
                sort={resolved.sort}
                totalLoans={resolved.totalLoans}
                displayedLoanHistory={resolved.loanHistory}
                emptyMessageKey="noLoanHistoryMessage"
                isRateAndReviewEnabled={true}
                requiresDateRange={true} // Triggers date range flow
                fetchPrintData={fetchLoanHistoryRange}
                isStudent={false}
              />
            </LSResultsSelectionProvider>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
